import { isNumber, type VehicleCardModel } from '@/features/vehicle';
import type { VehicleSortKey } from './vehicleListing.constants';

type Comparator = (a: VehicleCardModel, b: VehicleCardModel) => number;

/** A missing value sorts last in either direction. */
function byNumber(pick: (vehicle: VehicleCardModel) => number | undefined, direction: 1 | -1): Comparator {
    return (a, b) => {
        const x = pick(a);
        const y = pick(b);

        if (!isNumber(x)) return isNumber(y) ? 1 : 0;
        if (!isNumber(y)) return -1;

        return (x - y) * direction;
    };
}

const byNewest: Comparator = (a, b) => (b.facets.listedAt ?? '').localeCompare(a.facets.listedAt ?? '');

const COMPARATORS: Record<VehicleSortKey, Comparator> = {
    newest: byNewest,
    priceAsc: byNumber((vehicle) => vehicle.facets.price, 1),
    priceDesc: byNumber((vehicle) => vehicle.facets.price, -1),
    registration: byNumber((vehicle) => vehicle.facets.registration, -1),
    mileage: byNumber((vehicle) => vehicle.facets.mileage, 1),
};

/** Vehicles without photos always come last, whatever the sort. Ties keep the stock order. */
export function sortVehicles(vehicles: VehicleCardModel[], sort: VehicleSortKey): VehicleCardModel[] {
    const compare = COMPARATORS[sort];
    const photoRank = (vehicle: VehicleCardModel) => (vehicle.image ? 0 : 1);

    return [...vehicles].sort((a, b) => photoRank(a) - photoRank(b) || compare(a, b));
}
